import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Shield, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useNavigation } from "@/hooks/useNavigation";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLanguage } from "@/contexts/LanguageContext";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { currentLanguage } = useLanguage();
  const { data: navItems, isLoading: navLoading } = useNavigation();
  const { data: settings } = useSiteSettings();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fallback navigation if database not loaded yet
  const fallbackNavigation = [
    { name: "Home", href: "/" },
    { name: "Producten", href: "/producten" },
    { name: "Over Ons", href: "/over-ons" },
    { name: "Nieuws", href: "/nieuws" },
    { name: "Contact", href: "/contact" },
  ];

  const navigation = navLoading || !navItems?.length 
    ? fallbackNavigation 
    : navItems.filter(item => !item.href.includes("partner")).map(item => ({
        name: item.name,
        href: item.href,
      }));

  const partnerLink = navItems?.find(item => item.href.includes("partner"));

  const distributorText: Record<string, string> = {
    nl: "OFFICIEEL STORMSHIELD DISTRIBUTEUR BENELUX",
    en: "OFFICIAL STORMSHIELD DISTRIBUTOR BENELUX",
    fr: "DISTRIBUTEUR OFFICIEL STORMSHIELD BENELUX",
  };

  const partnerButtonText: Record<string, string> = {
    nl: "Partner Worden",
    en: "Become Partner",
    fr: "Devenir Partenaire",
  };

  const isActive = (href: string) => location.pathname === href;

  const phone = settings?.phone || "";
  const email = settings?.email || "info@cyscom.nl";

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Top Bar - Contact Info - Fixed height to prevent CLS */}
      <div className="bg-[hsl(var(--hero-background))] text-[hsl(var(--hero-foreground))] h-10 hidden md:flex items-center">
        <div className="container flex justify-between items-center text-sm">
          <div className="flex items-center gap-6 min-w-[280px]">
            <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-primary transition-colors">
              <Phone className="h-4 w-4 flex-shrink-0" />
              <span className="whitespace-nowrap">{phone}</span>
            </a>
            <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-primary transition-colors">
              <Mail className="h-4 w-4 flex-shrink-0" />
              <span className="whitespace-nowrap">{email}</span>
            </a>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/20 border border-primary/30">
              <Shield className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="font-semibold text-primary tracking-wide whitespace-nowrap">
                {distributorText[currentLanguage] || distributorText.nl}
              </span>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <nav 
        className={`w-full border-b transition-all duration-200 ${
          isScrolled 
            ? "border-border bg-card shadow-md" 
            : "border-border bg-card"
        }`}
      >
        <div className="container flex h-20 items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Shield className="h-10 w-10 text-primary" />
            <div className="flex flex-col">
              <span className="text-2xl font-bold tracking-tight text-foreground">CYSCOM</span>
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Stormshield Distributeur Benelux</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-semibold uppercase tracking-wide transition-colors relative py-2 ${
                  isActive(item.href)
                    ? "text-primary"
                    : "text-foreground hover:text-primary"
                }`}
              >
                {item.name}
                {isActive(item.href) && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </Link>
            ))}
            <Button size="lg" asChild>
              <Link to={partnerLink?.href || "/partner-worden"}>
                {partnerLink?.name || partnerButtonText[currentLanguage] || partnerButtonText.nl}
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher />
            <button
              className="text-foreground p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-border bg-card">
            <div className="container py-4 flex flex-col gap-4">
              {/* Mobile Contact Info */}
              <div className="flex flex-col gap-2 pb-4 border-b border-border">
                <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
                  <Phone className="h-4 w-4" />
                  <span>{phone}</span>
                </a>
                <a href={`mailto:${email}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
                  <Mail className="h-4 w-4" />
                  <span>{email}</span>
                </a>
              </div>
              
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-base font-semibold uppercase tracking-wide transition-colors ${
                    isActive(item.href)
                      ? "text-primary"
                      : "text-foreground hover:text-primary"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <Button asChild className="w-full mt-2">
                <Link to={partnerLink?.href || "/partner-worden"} onClick={() => setIsMenuOpen(false)}>
                  {partnerLink?.name || partnerButtonText[currentLanguage] || partnerButtonText.nl}
                </Link>
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
